import { PurchaseCondition, PurchaseConditionDuplicate } from '@/models/purchase-condition/purchase-condition';
import { PurchaseConditionsGroupByCircuit } from '@/models/purchase-condition/purchase-conditions-group-by-circuit';
import { SupplierInfo } from '@/models/purchase-condition/selection/supplier-info';
import { notifyError, notifyInfo, notifySuccess } from '@/modules/notification-center';
import I18n from '@/plugins/i18n';
import { performSelectionsUpdate } from '@/resources/apollo/mutations/selection';
import { getPurchaseConditionsGroupByCircuit } from '@/resources/apollo/queries/purchase-conditions-group-by-circuit';
import { getSupplierInfo } from '@/resources/apollo/queries/supplier-info';

export async function loadPurchaseConditions(supplierCode: number): Promise<PurchaseConditionsGroupByCircuit | null> {
  const filters = { supplierCode };
  try {
    return await getPurchaseConditionsGroupByCircuit(filters);
  } catch (error) {
    notifyError(I18n.t('notification.selection.getSupplierPurchaseConditionsFailed'));
    return null;
  }
}

export async function searchSupplierInfo(
  supplierCode?: number,
  supplierCorpName?: string
): Promise<SupplierInfo | null> {
  const filters = { supplierCode, supplierCorpName };
  try {
    const supplierInfo = await getSupplierInfo(filters);
    if (supplierInfo === null) {
      notifyInfo(I18n.t('notification.selection.supplierNotFound'));
    }
    return supplierInfo;
  } catch (error) {
    notifyError(I18n.t('notification.selection.getSupplierFailure'));
    return null;
  }
}

export async function saveModifications(selectedIds: string[], unselectedIds: string[]): Promise<void> {
  try {
    const { selectedCount, unselectedCount } = await performSelectionsUpdate(selectedIds, unselectedIds);
    let message = I18n.tc('notification.selection.modificationSuccess.base', selectedCount + unselectedCount);
    message += ' (' + I18n.tc('notification.selection.modificationSuccess.selectionDetails', selectedCount) + ', ';
    message += I18n.tc('notification.selection.modificationSuccess.unselectionDetails', unselectedCount) + ')';
    notifySuccess(message);
  } catch (error) {
    notifyError(I18n.t('notification.selection.modificationFailed'));
    throw error;
  }
}

export function extractDuplicates(
  purchaseConditionsByCircuit: PurchaseConditionsGroupByCircuit
): PurchaseConditionDuplicate[] {
  const purchaseConditions: PurchaseCondition[] = Object.values(purchaseConditionsByCircuit)
    .filter((circuit): circuit is PurchaseCondition[] => circuit != null)
    .flat();
  const duplicatesMap = purchaseConditions.reduce(duplicatesMapReducer, {});

  return Object.entries(duplicatesMap)
    .filter(([, value]) => value.length > 1)
    .map(([key, value]) => {
      return value.map(purchaseCondition => ({
        ...purchaseCondition,
        duplicateKey: key
      }));
    })
    .flat();
}

function duplicatesMapReducer(
  duplicatesMap: Record<string, PurchaseCondition[]>,
  purchaseCondition: PurchaseCondition
): Record<string, PurchaseCondition[]> {
  const duplicateKey = getDuplicateKey(purchaseCondition);
  if (duplicatesMap[duplicateKey] === undefined) {
    duplicatesMap[duplicateKey] = [];
  }
  duplicatesMap[duplicateKey].push(purchaseCondition);
  return duplicatesMap;
}

function getDuplicateKey({ supplierCode, ean, ref, circuit, beginDate }: PurchaseCondition): string {
  return `${supplierCode}::${ean}::${ref}::${circuit}::${beginDate}`;
}
